import api from './axiosInstance';

// Generador de Templates Seccionados (EEPP). Backend: TemplateGeneratorController
// (api/template-generator). Un template por tipo de escritura; el usuario elige el
// tipo y name/description se derivan del catálogo.

/** Tipos de escritura activos con el estado de su template (selector + lista por tipo). */
export const getWritingTypes = async (signal) => {
  const response = await api.get('/template-generator/writing-types', { signal });
  return response.data || [];
};

/** Versiones (activas e inactivas) de un template por Name, más recientes primero. */
export const getTemplateVersions = async (name, signal) => {
  try {
    const response = await api.get(
      `/template-generator/templates/${encodeURIComponent(name)}/versions`,
      { signal }
    );
    return response.data || [];
  } catch (error) {
    if (error?.response?.status === 404) return [];
    throw error;
  }
};

/** Ledger de aprendizaje (observabilidad / timeline). */
export const getLearningEvents = async (take = 50, signal) => {
  const response = await api.get('/template-generator/learning-events', {
    params: { take },
    signal,
  });
  return response.data || [];
};

/** Crea un template desde archivos subidos (multipart). writingTypeId basta; name/description se derivan. */
export const createTemplateFromFiles = async ({ writingTypeId, files }, signal) => {
  const form = new FormData();
  form.append('writingTypeId', String(writingTypeId));
  (files || []).forEach((f) => form.append('files', f));
  const response = await api.post('/template-generator/from-files', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  });
  return response.data;
};

/** Procesa AHORA el lote de correcciones encoladas (QUEUED) del template. */
export const learnNow = async (name, signal) => {
  const response = await api.post(
    `/template-generator/learn-now/${encodeURIComponent(name)}`,
    null,
    { signal }
  );
  return response.data;
};

/** Edición manual de las secciones del template → nueva versión activa (SourceType=MANUAL). */
export const updateTemplateSections = async (name, sectionsJson, signal) => {
  const response = await api.put(
    `/template-generator/templates/${encodeURIComponent(name)}/sections`,
    { sectionsJson },
    { signal }
  );
  return response.data;
};

/** Activa una versión específica (rollback). Endpoint en WritingsController. */
export const activateTemplateVersion = async (templateId, signal) => {
  const response = await api.post(`/writings/templates/${templateId}/activate`, null, { signal });
  return response.data;
};

/** Crea un tipo de escritura nuevo en el catálogo. tiempos = peso de carga (null permitido). */
export const createWritingType = async ({ code, displayName, familyJuridica, tiempos }, signal) => {
  const response = await api.post(
    '/template-generator/writing-types',
    { code, displayName, familyJuridica, tiempos },
    { signal }
  );
  return response.data;
};

/** Edita el nombre visible y el peso de carga de un tipo (Code/familia inmutables). */
export const updateWritingType = async (id, { displayName, tiempos }, signal) => {
  const response = await api.put(
    `/template-generator/writing-types/${id}`,
    { displayName, tiempos },
    { signal }
  );
  return response.data;
};

/** Activa o desactiva un tipo de escritura. */
export const setWritingTypeActive = async (id, active, signal) => {
  const action = active ? 'activate' : 'deactivate';
  const response = await api.post(`/template-generator/writing-types/${id}/${action}`, null, { signal });
  return response.data;
};

// ── Catálogo de documentos requeridos ──────────────────────────────────────

/** Catálogo de documentos. includeInactive=true incluye los desactivados. */
export const getRequiredDocuments = async (includeInactive = false, signal) => {
  const response = await api.get('/template-generator/required-documents', {
    params: { includeInactive },
    signal,
  });
  return response.data || [];
};

/** Crea un documento del catálogo (Name único). */
export const createRequiredDocument = async ({ name, sourceAlias, sourceUrl }, signal) => {
  const response = await api.post(
    '/template-generator/required-documents',
    { name, sourceAlias, sourceUrl },
    { signal }
  );
  return response.data;
};

/** Edita un documento del catálogo (incl. isActive = soft-delete). */
export const updateRequiredDocument = async (id, { name, sourceAlias, sourceUrl, isActive }, signal) => {
  const response = await api.put(
    `/template-generator/required-documents/${id}`,
    { name, sourceAlias, sourceUrl, isActive },
    { signal }
  );
  return response.data;
};

// ── Documentos requeridos por tipo de escritura ─────────────────────────────

/** Documentos requeridos asignados a un tipo. */
export const getWritingTypeDocuments = async (writingTypeId, signal) => {
  const response = await api.get(
    `/template-generator/writing-types/${writingTypeId}/documents`,
    { signal }
  );
  return response.data || [];
};

/** Reemplaza el set completo de documentos requeridos de un tipo. items: [{ requiredDocumentId, roleQualifier }]. */
export const setWritingTypeDocuments = async (writingTypeId, items, signal) => {
  const response = await api.put(
    `/template-generator/writing-types/${writingTypeId}/documents`,
    { items },
    { signal }
  );
  return response.data || [];
};

// ── Catálogo de flags de intake ─────────────────────────────────────────────

/** Catálogo de flags jurídicos. includeInactive=true incluye los desactivados. */
export const getIntakeFlags = async (includeInactive = false, signal) => {
  const response = await api.get('/template-generator/intake-flags', {
    params: { includeInactive },
    signal,
  });
  return response.data || [];
};

/** Crea un flag del catálogo (Title único). */
export const createIntakeFlag = async ({ title, instruction, severity, scope }, signal) => {
  const response = await api.post(
    '/template-generator/intake-flags',
    { title, instruction, severity, scope },
    { signal }
  );
  return response.data;
};

/** Edita un flag del catálogo (incl. isActive = soft-delete). */
export const updateIntakeFlag = async (id, { title, instruction, severity, scope, isActive }, signal) => {
  const response = await api.put(
    `/template-generator/intake-flags/${id}`,
    { title, instruction, severity, scope, isActive },
    { signal }
  );
  return response.data;
};
